import React, { useContext, useCallback, useState, useMemo, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Spinner,
  Text,
  MessageBar,
  MessageBarType,
} from '@bingads-webui-react/fabric-control';
import _, { isEmpty } from 'underscore';
import { keyCode } from '@bingads-webui/keycode';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  ItemPlaceholderBoxView,
  videoIcon,
} from '@bingads-webui-campaign-react/asset-library-components';
import { Video } from '@bingads-webui-campaign-react/video';

import { getPreviewStyles } from './styles';
import { panelNames } from './state';
import { loadImagePromise } from '../util';

const PanelButton = ({ panel, currentPanel, switchPanel }) => {
  const {
    name,
    text,
  } = panel;

  const clickFunc = useCallback(() => {
    switchPanel(name);
  }, [name, switchPanel]);

  const onKeyDown = useCallback((ev) => {
    if (ev.keyCode === keyCode.Enter) {
      clickFunc();
    }
  }, [clickFunc]);

  const isSelected = name === currentPanel;

  return (
    <div
      className={classNames('panel-button', { selected: isSelected })}
      onClick={clickFunc}
      role="button"
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <Text variant="medium" >{text}</Text>
    </div>
  );
};

PanelButton.propTypes = {
  panel: PropTypes.shape({}).isRequired,
  currentPanel: PropTypes.string.isRequired,
  switchPanel: PropTypes.func.isRequired,
};

const Switch = ({
  currentPanel, switchPanel,
}) => {
  const i18n = useContext(I18nContext);
  const panels = [
    { name: panelNames.scene, text: i18n.getString(_TL_('Scene')) },
    { name: panelNames.video, text: i18n.getString(_TL_('Video')) },
  ];

  return (
    <div className="panel-switch">
      {panels.map(panel => (<PanelButton
        key={panel.name}
        panel={panel}
        currentPanel={currentPanel}
        switchPanel={switchPanel}
      />))}
    </div>
  );
};

Switch.propTypes = {
  currentPanel: PropTypes.string.isRequired,
  switchPanel: PropTypes.func.isRequired,
};

const ScenePanel = ({ scene, setGenerateError }) => {
  const {
    keyFrame,
  } = scene;

  const i18n = useContext(I18nContext);

  useEffect(() => {
    if (keyFrame && keyFrame.url) {
      loadImagePromise(keyFrame.url, false).then((result) => {
        if (result === 'failed') {
          setGenerateError(i18n.getString(_TL_('The scene\'s image failed to load. Select Generate to update the video with a new scene.')));
        } else {
          setGenerateError(undefined);
        }
      });
    }
  }, [i18n, keyFrame, setGenerateError]);
  return (
    <div className="scene-panel">
      {keyFrame && keyFrame.url && <img src={keyFrame.url} alt="scene" />}
    </div>
  );
};

ScenePanel.propTypes = {
  scene: PropTypes.shape({}).isRequired,
  setGenerateError: PropTypes.func.isRequired,
};

const PlaceHolderBoxViewFooterConfig = {
  text: {
    summary: _TL_('Your video is currently loading.'),
  },
};

const VideoPanel = ({ video }) => {
  const i18n = useContext(I18nContext);
  const [isLoading, setIsLoading] = useState(true);

  const footerProps = useMemo(() => {
    const localizedTextFields = _.mapObject(
      PlaceHolderBoxViewFooterConfig.text,
      text => i18n.getString(text)
    );

    return {
      ...localizedTextFields,
    };
  }, [i18n]);

  const notReady = isLoading;

  const {
    url, src,
  } = video;

  return (
    <div className="video-panel">
      {notReady &&
        <ItemPlaceholderBoxView
          className="video-placeholder"
          footerProps={footerProps}
          mainViewIcon={videoIcon}
          mainViewIconLabel={i18n.getString(_TL_('Video'))}
        />}
      <div className={classNames('video-wrapper', notReady ? 'hidden' : '')}>
        <Video
          showTime
          onVideoLoaded={() => setIsLoading(false)}
          i18n={i18n}
          ariaLabel={i18n.getString(_TL_('Generated Video'))}
          src={url || src}
        />
      </div>
    </div>
  );
};

const LoadingBlocker = ({ videoGenerated }) => {
  const i18n = useContext(I18nContext);
  const loadingText = videoGenerated === 0 || videoGenerated === 10 ? _TL_('Generating a new video...') : _TL_('Generating a new scene...');
  return (
    <Fragment>
      <div className="loading-placeholder" />
      <div className="spinner-box">
        <Spinner />
        <span className="spinner-text">{i18n.getString(loadingText)}</span>
      </div>
    </Fragment>
  );
};

const Panels = ({
  currentPanel, switchPanel, isScenePanel, scene, video, videoGenerated, sceneGenerated, generateError, setGenerateError,
}) => {
  const renderPreview = useCallback(() => {
    if (isScenePanel && !isEmpty(scene)) {
      if (sceneGenerated === 0) {
        return <LoadingBlocker videoGenerated={videoGenerated} />;
      }
      return <ScenePanel scene={scene} setGenerateError={setGenerateError} />;
    }
    if (videoGenerated === 0 || videoGenerated === 10) {
      return <LoadingBlocker videoGenerated={videoGenerated} />;
    }
    return <VideoPanel video={video} />;
  }, [isScenePanel, scene, sceneGenerated, video, videoGenerated, setGenerateError]);

  return (
    <Fragment>
      {!isEmpty(scene) && <Switch currentPanel={currentPanel} switchPanel={switchPanel} />}
      {renderPreview()}
      <div className="message-bar">
        {
          generateError &&
          <MessageBar
            messageBarType={MessageBarType.error}
            isMultiline={false}
            onDismiss={() => setGenerateError(undefined)}
            dismissButtonAriaLabel="Close"
          >
            {generateError}
          </MessageBar>
        }
      </div>
    </Fragment>
  );
};


Panels.propTypes = {
  currentPanel: PropTypes.string.isRequired,
  switchPanel: PropTypes.func.isRequired,
  isScenePanel: PropTypes.bool.isRequired,
  scene: PropTypes.shape({}).isRequired,
  video: PropTypes.shape({}).isRequired,
  videoGenerated: PropTypes.number.isRequired,
  sceneGenerated: PropTypes.number.isRequired,
  generateError: PropTypes.string,
  setGenerateError: PropTypes.func.isRequired,
};

Panels.defaultProps = {
  generateError: undefined,
};

export const PreviewView = ({
  scene, video, videoGenerated, sceneGenerated, currentPanel, switchPanel, isScenePanel, generateError, setGenerateError,
}) => {
  const classes = useDefaultStyles(getPreviewStyles);
  return (
    <div className={classNames(classes.preview, 'preview')}>
      <Panels
        currentPanel={currentPanel}
        switchPanel={switchPanel}
        isScenePanel={isScenePanel}
        scene={scene}
        video={video}
        sceneGenerated={sceneGenerated}
        videoGenerated={videoGenerated}
        generateError={generateError}
        setGenerateError={setGenerateError}
      />
    </div>
  );
};

PreviewView.propTypes = {
  scene: PropTypes.shape({}),
  video: PropTypes.shape({}).isRequired,
  currentPanel: PropTypes.string.isRequired,
  switchPanel: PropTypes.func.isRequired,
  isScenePanel: PropTypes.bool.isRequired,
  videoGenerated: PropTypes.number.isRequired,
  sceneGenerated: PropTypes.number.isRequired,
  generateError: PropTypes.string,
  setGenerateError: PropTypes.func.isRequired,
};

PreviewView.defaultProps = {
  scene: {},
  generateError: undefined,
};

